/**
 * TwinNetworkSections (iter1068) — обновените секции за началната страница.
 * Имената и броят на близнаците се четат от единния каталог
 * (`lib/twinCatalog.js`), за да не остарява отново текстът.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowRight, CircleDot, Mic, Map, Utensils, CalendarCheck,
  GitBranch, ShieldCheck, Network,
} from "lucide-react";
import { TWIN_CATALOG, TWIN_IDS } from "@/lib/twinCatalog";
import { TWIN_DESC, CIRCLES, FEATURES, pick, twinLabel } from "./twinNetworkCopy";

const GROUP_IDS = {
  all: TWIN_IDS,
  organs: ["metabolic_insulin", "liver_kidney", "kidney", "cardio",
    "inflammation", "hematology", "respiratory"],
  lifestyle: ["nutritional", "movement", "sleep", "stress", "body_comp", "microbiome"],
  clinical: ["hormonal", "bhi", "male_health", "dental", "neuro", "lab", "phenotype"],
};

const DEFAULT_TWINS_NET = {
  eyebrow: "Физиологична архитектура",
  title_prefix: "близнака за отделни системи. Едно общо равновесие.",
  sub: "Всеки близнак отговаря за точно определена система и получава само съответните данни. Заедно образуват жива мрежа, в която нито един показател не се губи и не се брои двукратно.",
  filter_all: "Всички близнаци",
  filter_organs: "Обмяна и органи",
  filter_lifestyle: "Начин на живот",
  filter_clinical: "Клинични и ендокринни",
};

const DEFAULT_CIRCLES_COPY = {
  eyebrow: "Системна биология",
  title: "Над 110 физиологични връзки и откриване на порочни кръгове",
  sub: "Напрежението не остава в нервната система — то потиска превръщането на хормоните на щитовидната жлеза. Кратката нощ мени отговора към инсулин още на следващия ден. Мрежата подрежда тези зависимости и показва кой кръг гори днес и къде е лостът.",
};

const DEFAULT_CAPABILITIES_COPY = {
  eyebrow: "Как работи в ежедневието",
  title: "Въведеното на едно място стига до всеки близнак, който го засяга",
  sub: "Няма раздел, който да съществува сам за себе си. Резултатът от изследване, едно хранене или едно отбелязване „днес“ променят оценките на близнаците, които зависят от тях — и всяка промяна носи причината си.",
  note_title: "Без скрити изчисления",
  note_body: "Всяка оценка се отваря и показва от какво е съставена: показателите, които близнакът оценява, показателите, които ползва като контекст, и разделите, които са я намалили — с причината за всяко намаление. Платформата подрежда собствените Ви данни и има образователна цел; тя не поставя диагноза и не заменя лекарски преглед.",
  cta_primary: "Създайте профил",
  cta_secondary: "Как работи мрежата",
};

const Eyebrow = ({ isDark, children }) => (
  <div className={`text-xs font-bold uppercase tracking-[0.2em] mb-3 ${
    isDark ? "text-teal-300" : "text-teal-700"}`}>{children}</div>
);

export function TwinNetworkSection({ isDark, copy, lang = "bg" }) {
  const desc = pick(TWIN_DESC, lang);
  const c = { ...DEFAULT_TWINS_NET, ...(copy || {}) };
  const groups = [
    { id: "all", label: c.filter_all },
    { id: "organs", label: c.filter_organs },
    { id: "lifestyle", label: c.filter_lifestyle },
    { id: "clinical", label: c.filter_clinical },
  ];
  const [group, setGroup] = useState("all");
  const ids = useMemo(
    () => (GROUP_IDS[group] || GROUP_IDS.all).filter((i) => TWIN_CATALOG[i]),
    [group],
  );
  return (
    <section data-testid="landing-twins-section"
      className={`py-20 sm:py-28 ${isDark ? "bg-[#090D16]" : "bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="max-w-3xl">
          <Eyebrow isDark={isDark}>{c.eyebrow}</Eyebrow>
          <h2 data-testid="landing-twins-title"
            className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight ${
              isDark ? "text-white" : "text-slate-900"}`}>
            {TWIN_IDS.length} {c.title_prefix}
          </h2>
          <p className={`text-base sm:text-sm leading-relaxed mt-4 ${
            isDark ? "text-slate-400" : "text-slate-600"}`}>
            {c.sub}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mt-8" data-testid="landing-twins-filters">
          {groups.map((g) => (
            <button key={g.id} type="button" onClick={() => setGroup(g.id)}
              data-testid={`landing-twin-filter-${g.id}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                group === g.id
                  ? isDark ? "bg-teal-500/15 border-teal-400/60 text-teal-200"
                    : "bg-teal-50 border-teal-500 text-teal-800"
                  : isDark ? "border-slate-800 text-slate-400 hover:text-slate-200"
                    : "border-slate-200 text-slate-600 hover:text-slate-900"}`}>
              {g.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6"
          data-testid="landing-twins-grid">
          {ids.map((id) => (
            <div key={id} data-testid={`landing-twin-card-${id}`}
              className={`rounded-xl p-4 border transition-transform hover:-translate-y-0.5 ${
                isDark ? "border-slate-800 bg-[#0F172A]" : "border-slate-200 bg-white shadow-sm"}`}>
              <div className="flex items-start gap-2">
                <CircleDot className={`w-4 h-4 mt-0.5 shrink-0 ${
                  isDark ? "text-teal-400" : "text-teal-700"}`} />
                <div className="min-w-0">
                  <div className={`text-sm font-bold leading-tight ${
                    isDark ? "text-white" : "text-slate-900"}`}>{twinLabel(id, lang, TWIN_CATALOG[id].label)}</div>
                  <p className={`text-xs leading-relaxed mt-1.5 ${
                    isDark ? "text-slate-400" : "text-slate-600"}`}>{desc[id]}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ViciousCirclesSection({ isDark, copy, lang = "bg" }) {
  const cc = { ...DEFAULT_CIRCLES_COPY, ...(copy || {}) };
  return (
    <section data-testid="landing-connections-section"
      className={`py-20 sm:py-28 ${isDark ? "bg-[#0F172A]" : "bg-[#F1F5F9]"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="max-w-3xl">
          <Eyebrow isDark={isDark}>{cc.eyebrow}</Eyebrow>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight ${
            isDark ? "text-white" : "text-slate-900"}`}>
            {cc.title}
          </h2>
          <p className={`text-base sm:text-sm leading-relaxed mt-4 ${
            isDark ? "text-slate-400" : "text-slate-600"}`}>
            {cc.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10">
          {(cc.items || pick(CIRCLES, lang)).map((c) => (
            <div key={c.id} data-testid={`landing-vicious-circle-${c.id}`}
              className={`rounded-2xl p-6 border ${
                isDark ? "border-slate-800 bg-[#090D16]" : "border-slate-200 bg-white shadow-sm"}`}>
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {c.title}
              </h3>
              <ol className="mt-4 space-y-2">
                {c.steps.map((s, i) => (
                  <li key={i} data-testid="landing-circle-step-item"
                    className={`flex gap-2.5 text-xs leading-relaxed ${
                      isDark ? "text-slate-300" : "text-slate-700"}`}>
                    <span className={`shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${
                      isDark ? "bg-slate-800 text-teal-300" : "bg-slate-100 text-teal-800"}`}>
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
              <p className={`text-xs mt-4 pt-4 border-t ${
                isDark ? "border-slate-800 text-emerald-300" : "border-slate-200 text-emerald-700"}`}>
                {c.lever}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEATURE_ICONS = [Mic, Map, Utensils, CalendarCheck, GitBranch, ShieldCheck];

export function PlatformCapabilitiesSection({ isDark, copy, lang = "bg" }) {
  const cp = { ...DEFAULT_CAPABILITIES_COPY, ...(copy || {}) };
  return (
    <section data-testid="landing-capabilities-section"
      className={`py-20 sm:py-28 ${isDark ? "bg-[#090D16]" : "bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="max-w-3xl">
          <Eyebrow isDark={isDark}>{cp.eyebrow}</Eyebrow>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight ${
            isDark ? "text-white" : "text-slate-900"}`}>
            {cp.title}
          </h2>
          <p className={`text-base sm:text-sm leading-relaxed mt-4 ${
            isDark ? "text-slate-400" : "text-slate-600"}`}>
            {cp.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10"
          data-testid="landing-capabilities-grid">
          {(cp.items || pick(FEATURES, lang)).map((f, fi) => {
            const Icon = f.icon || FEATURE_ICONS[fi] || Activity;
            return (
              <div key={f.id} data-testid={`landing-capability-${f.id}`}
                className={`rounded-2xl p-6 border ${
                  isDark ? "border-slate-800 bg-[#0F172A]" : "border-slate-200 bg-white shadow-sm"}`}>
                <div className={`w-10 h-10 rounded-xl grid place-items-center mb-4 ${
                  isDark ? "bg-teal-500/10 text-teal-300" : "bg-teal-50 text-teal-700"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {f.title}
                </h3>
                <p className={`text-xs leading-relaxed mt-2 ${
                  isDark ? "text-slate-400" : "text-slate-600"}`}>{f.text}</p>
              </div>
            );
          })}
        </div>

        <div className={`rounded-2xl p-6 sm:p-8 mt-8 border ${
          isDark ? "border-slate-800 bg-[#0F172A]" : "border-slate-200 bg-[#F1F5F9]"}`}
          data-testid="landing-transparency-note">
          <div className="flex items-start gap-3">
            <Activity className={`w-5 h-5 mt-0.5 shrink-0 ${
              isDark ? "text-emerald-300" : "text-emerald-700"}`} />
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {cp.note_title}
              </h3>
              <p className={`text-xs leading-relaxed mt-2 ${
                isDark ? "text-slate-400" : "text-slate-600"}`}>
                {cp.note_body}
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <Link to="/register" data-testid="landing-capabilities-primary-cta"
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 ${
                    isDark ? "bg-emerald-600 hover:bg-emerald-500" : "bg-[#15803D] hover:bg-[#166534]"}`}>
                  {cp.cta_primary} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/smart/digital-twin" data-testid="landing-capabilities-secondary-cta"
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold border ${
                    isDark ? "border-slate-700 text-slate-200 hover:bg-white/5"
                      : "border-slate-300 text-slate-800 hover:bg-white"}`}>
                  <Network className="w-4 h-4" /> {cp.cta_secondary}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TwinNetworkSection;
